import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation, matchPath } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Types
import { Feature } from 'Shared/types/Organization.type';

// Hooks
import { useToggle, useMembers, useQueryParam, useFeature } from 'Shared/utils/hooks';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Actions
import * as goalsActions from 'Shared/actions/Goals.actions';

// Selectors
import * as GoalsSelectors from 'Shared/selectors/Goals.selectors';

// Components
import { WpPageLayout, WpPageHeader, WpPageBody, WpPageTitle } from 'Wpds/components/page-layout';
import Spinner from 'Shared/components/Spinner';
import GoalsContainer from 'Shared/components/Goals/GoalsContainer';
import GoalsContainerV3 from 'Shared/components/Goals/GoalsContainerV3';
import { WpButtonGroup } from 'Wpds/components/button';
import ExportGoalsButton from './ExportGoalsButton/ExportGoalsButton';
import CreateGoalButtonContainer from 'Shared/components/Goals/CreateGoalButtonContainer/CreateGoalButtonContainer';
import GoalsEmptyState from 'Shared/components/Goals/EmptyState/GoalsEmptyState';
import CreateGoalModal from '../CreateOrUpdateGoalPageV3/CreateGoalModal';
import FreePlanGoalsContent from 'Shared/components/FreePlanGoalsContent/FreePlanGoalsContent';

const GoalsPage = () => {
  const { featureFlagGoalsVersion3: showNewGoals, featureFlagGoalsDetailPanel: showNewPanel } = useFlags();
  const { isFreePlan } = useFeature(Feature.goals);

  const [show] = useQueryParam('show');

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const divRef = useRef(null);

  const [isLoading, toggleIsLoading] = useToggle(false);
  const [isCreateModalOpen, toggleIsCreateModalOpen] = useToggle(false);
  const { isLoading: isLoadingMembers } = useMembers();
  const members = useSelector(state => state.members) || [];

  const goals = useSelector(state => GoalsSelectors.getAllGoals(state));

  const handleLaunchModalDetails = pathname => {
    history.push({
      pathname: pathname,
      state: { modal: true },
    });
  };

  useEffect(() => {
    toggleIsLoading();
    const params = {
      context: 'team',
    };
    dispatch(goalsActions.fetchGoals(params)).finally(() => toggleIsLoading());
  }, []);

  useEffect(() => {
    const isModalPath = !!matchPath(location.pathname, { path: PageUrls.GOALS_PAGE_MODAL });
    if (isModalPath) {
      handleLaunchModalDetails(location.pathname);
    }
  }, []);

  useEffect(() => {
    if (show === 'new') toggleIsCreateModalOpen();
    history.replace(PageUrls.GOALS_PAGE);
  }, []);

  const onItemClick = goal => {
    showNewPanel
      ? history.push(PageUrlsUtil.makeGoalDetailPanelUrl(goal.slug))
      : handleLaunchModalDetails(PageUrlsUtil.makeGoalDetailPageUrl(goal.slug));
  };

  const stillLoading = isLoading || isLoadingMembers;
  const activeUsersIds = members.map(user => user.id);
  const goalsToRender = goals.filter(goal => activeUsersIds.indexOf(goal.get('assigneeId')) > -1);

  const hasGoals = !stillLoading && goalsToRender.size > 0;
  const showEmpty = !stillLoading && goalsToRender.size === 0;

  return (
    <>
      <WpPageLayout width="full" className="TeamPage">
        <WpPageHeader slim sticky>
          <WpPageTitle
            title="Goals"
            buttonsOnMobile="inline"
            buttonsContainer={
              !isFreePlan && (
                <WpButtonGroup>
                  <CreateGoalButtonContainer />
                  <ExportGoalsButton />
                </WpButtonGroup>
              )
            }
          />
        </WpPageHeader>
        {!isFreePlan ? (
          <WpPageBody>
            <div ref={divRef}>
              {stillLoading && <Spinner activeIf />}
              {hasGoals &&
                (showNewGoals ? (
                  <GoalsContainerV3 goals={goalsToRender} onItemClick={onItemClick} />
                ) : (
                  <GoalsContainer goals={goalsToRender} onItemClick={onItemClick} />
                ))}
              {showEmpty && <GoalsEmptyState variant="teamOpenNoneCompleted" />}
            </div>
          </WpPageBody>
        ) : (
          <WpPageBody>
            <FreePlanGoalsContent />
          </WpPageBody>
        )}
      </WpPageLayout>
      {!isFreePlan && <CreateGoalModal isOpen={isCreateModalOpen} toggleIsOpen={toggleIsCreateModalOpen} />}
    </>
  );
};

export default GoalsPage;
